import {
  PasswordResetFlowContextType,
  PasswordResetFlowState,
  PasswordResetStep,
} from '@repo/ui';
import React, { createContext, ReactNode, useContext, useReducer } from 'react';

type PasswordResetFlowAction =
  | { type: 'SET_CPF'; payload: string }
  | { type: 'SET_CODE_VERIFIED' }
  | { type: 'RESET_FLOW' };

const initialState: PasswordResetFlowState = {
  currentStep: 'cpf',
  cpf: null,
  codeVerified: false,
  completedSteps: [],
};

function passwordResetFlowReducer(
  state: PasswordResetFlowState,
  action: PasswordResetFlowAction,
): PasswordResetFlowState {
  switch (action.type) {
    case 'SET_CPF':
      return {
        ...state,
        cpf: action.payload,
        currentStep: 'code',
        completedSteps: ['cpf'],
      };
    case 'SET_CODE_VERIFIED':
      return {
        ...state,
        codeVerified: true,
        currentStep: 'password',
        completedSteps: ['cpf', 'code'],
      };
    case 'RESET_FLOW':
      return initialState;
    default:
      return state;
  }
}

const PasswordResetFlowContext = createContext<
  PasswordResetFlowContextType | undefined
>(undefined);

interface PasswordResetFlowProviderProps {
  children: ReactNode;
}

export function PasswordResetFlowProvider({
  children,
}: PasswordResetFlowProviderProps) {
  const [state, dispatch] = useReducer(passwordResetFlowReducer, initialState);

  const setCpf = (cpf: string) => {
    dispatch({ type: 'SET_CPF', payload: cpf });
  };

  const setCodeVerified = () => {
    dispatch({ type: 'SET_CODE_VERIFIED' });
  };

  const resetFlow = () => {
    dispatch({ type: 'RESET_FLOW' });
  };

  const canAccessStep = (step: PasswordResetStep): boolean => {
    switch (step) {
      case 'cpf':
        return true; // Primeira etapa sempre acessível
      case 'code':
        return state.completedSteps.includes('cpf') && !!state.cpf;
      case 'password':
        return state.completedSteps.includes('code') && state.codeVerified;
      default:
        return false;
    }
  };

  return (
    <PasswordResetFlowContext.Provider
      value={{
        state,
        setCpf,
        setCodeVerified,
        resetFlow,
        canAccessStep,
      }}
    >
      {children}
    </PasswordResetFlowContext.Provider>
  );
}

export function usePasswordResetFlow(): PasswordResetFlowContextType {
  const context = useContext(PasswordResetFlowContext);
  if (context === undefined) {
    throw new Error(
      'usePasswordResetFlow must be used within a PasswordResetFlowProvider',
    );
  }
  return context;
}
